/**
 * Shared catalogue of MCP feature flag constants and the `isMcpFlag`
 * narrowing predicate.
 *
 * This file is the *internal* source for the catalogue. `index.ts` (the
 * public entrypoint) re-exports everything from here. Helpers in the
 * package — `applyMcpFlagDelta.ts`, `validateMcpFlagSet.ts`, etc. —
 * import directly from this module rather than from `index.ts` so we
 * avoid a circular dependency (`index → helper → index`) that's fragile
 * under CommonJS/conditional-exports module resolution.
 *
 * Public consumers should still import from `@prerender/mcp-flags`
 * directly; nothing here is part of the package's public API contract
 * beyond what `index.ts` re-exports.
 */

export const MCP_FLAGS = {
  MCP_ENABLED: 'mcp',
  MCP_WRITE: 'mcp-write',
  MCP_AUTOMATED_TIER: 'mcp-automated-tier',
} as const;

export type McpFlag = typeof MCP_FLAGS[keyof typeof MCP_FLAGS];

export const MCP_FLAG_VALUES: readonly McpFlag[] = [
  MCP_FLAGS.MCP_ENABLED,
  MCP_FLAGS.MCP_WRITE,
  MCP_FLAGS.MCP_AUTOMATED_TIER,
] as const;

export const MCP_FLAG_DEPENDENCIES: Record<McpFlag, readonly McpFlag[]> = {
  [MCP_FLAGS.MCP_ENABLED]: [],
  [MCP_FLAGS.MCP_WRITE]: [MCP_FLAGS.MCP_ENABLED],
  [MCP_FLAGS.MCP_AUTOMATED_TIER]: [MCP_FLAGS.MCP_ENABLED],
};

export function isMcpFlag(value: unknown): value is McpFlag {
  return typeof value === 'string' && (MCP_FLAG_VALUES as readonly string[]).includes(value);
}
